import { Component, OnDestroy, OnInit } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

import { Events } from '../../helpers/events';

// ionic
import { ModalController, PopoverController } from '@ionic/angular';
// import { PopoverController, Platform } from '@ionic/angular';

// other libraries
// import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject } from "rxjs";
import { debounceTime, take, takeUntil } from "rxjs/operators";

// components

// providers
import { ChatGroupsService } from "../../services/chat-groups.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";
import { PaginatorService } from "../../services/paginator.service";
import { UserService } from "../../services/user.service";

// models
import { ChatGroup } from "../../models/chat-group";
import { Participant } from "../../models/participant";
import { User } from "../../models/user";

@Component({
    selector: 'chat-group-settings',
    templateUrl: './chat-group-settings.component.html',
    styleUrls: ['./chat-group-settings.component.scss']
})
export class ChatGroupSettingsComponent implements OnInit, OnDestroy {

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public data

    /**
     * chat group
     *
     * @type string
     */
    @Input() chatGroup: ChatGroup;

    /**
     * check if participant is moderator of chat group
     *
     * @type {boolean}
     */
    public isChatGroupModerator: boolean = false;

    // handlers for events
    chatGroupSelectedHandler;

    /**
     * constructor
     *
     * @param eventService
     * @param appEvents
     */
    constructor(
        public translate: TranslateService,
        public appEvents: Events,
        public plt: PlatformService,
        public overlayService: OverlayService,
        public paginatorService: PaginatorService,
        public chatGroupsService: ChatGroupsService,
        public modalController: ModalController,
        public popoverController: PopoverController,
        public userService: UserService
    ) {
        this.userLang = this.translate.currentLang;
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        // subscribe to current user
        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {
                this.user = user;

                // unsubscribe previous handler
                if (this.chatGroupSelectedHandler) {
                    this.appEvents.unsubscribe('messages:chatgroup', this.chatGroupSelectedHandler);
                }

                // attach new one
                this.chatGroupSelectedHandler = (selectedChatGroup?: ChatGroup) => {
                    this.chatGroup = new ChatGroup(selectedChatGroup);
                }

                this.appEvents.subscribe('messages:chatgroup', this.chatGroupSelectedHandler);

            } else {
                this.user = null;
            }
        });
    }

    /**
     * mute notifications from chat group
     */
    public muteNotifications() {
        if (this.chatGroup) {
            this.loading = true;

            let subscription = this.chatGroupsService.muteNotifications(this.chatGroup.id).subscribe(
                (response) => {
                    this.chatGroup.pivot_notifications_muted = true;
                    this.loading = false;
                },
                (error) => {
                    this.loading = false;

                    const data = error.error;
                    this.overlayService.showError(data.message);
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * unmute notifications from chat group
     */
    public unmuteNotifications() {
        if (this.chatGroup) {
            this.loading = true;

            let subscription = this.chatGroupsService.unmuteNotifications(this.chatGroup.id).subscribe(
                (response) => {
                    this.chatGroup.pivot_notifications_muted = false;
                    this.loading = false;
                },
                (error) => {
                    this.loading = false;

                    const data = error.error;
                    this.overlayService.showError(data.message);
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        if (this.chatGroupSelectedHandler) {
            this.appEvents.unsubscribe('messages:chatgroup', this.chatGroupSelectedHandler);
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
